import { useState, useEffect } from 'react'
import { format, isBefore, startOfDay } from 'date-fns'
import { toDisplayDate, isPastDate } from '@/lib/utils'
import { Filter, Calendar as CalendarIcon, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/ui/date-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ActivityTimelineItem } from './ActivityTimelineItem'
import { TaskDetailModal } from '@/components/TaskDetailModal'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useActivityTimeline, useUpdateTask } from '@/hooks/use-api'
import { socketClient } from '@/lib/socket'
import { useQueryClient } from '@tanstack/react-query'
import type { ActivityType, Activity } from '@/types'
import { explainError } from '@/lib/explain-error'

interface ActivityTimelineProps {
  entityId: string
  entityType: 'Account' | 'Contact' | 'Opportunity' | 'Lead'
  showFilters?: boolean
  limit?: number
}

export function ActivityTimeline({
  entityId,
  entityType,
  showFilters = true,
  limit = 25,
}: ActivityTimelineProps) {
  const [page, setPage] = useState(1)
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([
    'task',
    'event',
    'email',
    'call',
  ])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)

  // One-click task completion from the status circle: click → confirm → complete →
  // follow-up task modal (mirrors the Tasks list flow).
  const [completing, setCompleting] = useState<Activity | null>(null)
  const [followUpTask, setFollowUpTask] = useState<Activity | null>(null)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)
  const updateTask = useUpdateTask()
  const { toast } = useToast()

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useActivityTimeline({
    entity_id: entityId,
    entity_type: entityType,
    page,
    limit,
    activity_types: activityTypes,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  })

  // Socket.IO real-time updates
  useEffect(() => {
    const handleActivityUpdate = () => {
      // Invalidate queries to refetch timeline
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    }

    socketClient.onEntityUpdate(handleActivityUpdate)

    return () => {
      socketClient.offEntityUpdate(handleActivityUpdate)
    }
  }, [queryClient])

  const activities = data?.data || []
  const stats = data?.stats
  const total = data?.total || 0
  const totalPages = Math.ceil(total / limit)

  const handleTypeToggle = (type: ActivityType) => {
    setActivityTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types
        if (prev.length === 1) return prev
        return prev.filter((t) => t !== type)
      }
      return [...prev, type]
    })
    setPage(1) // Reset to first page when filter changes
  }

  const handleEditTask = (taskId: string) => {
    setEditingTaskId(taskId)
    setTaskModalOpen(true)
  }

  const handleConfirmComplete = async () => {
    const act = completing
    setCompleting(null)
    if (!act) return
    try {
      await updateTask.mutateAsync({ id: act.id, data: { status: 'Completed' } })
      toast({ title: 'Task completed', description: act.subject })
      // Continue the normal flow: prompt to create a follow-up task.
      setFollowUpTask(act)
      setFollowUpModalOpen(true)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to complete task'),
        variant: 'destructive',
      })
    }
  }

  const handleClearDateFilters = () => {
    setStartDate('')
    setEndDate('')
    setPage(1)
  }

  // Split activities into Upcoming & Overdue vs History (Salesforce-style)
  const today = startOfDay(new Date())

  const isUpcomingOrOverdue = (activity: Activity): boolean => {
    // Open tasks (not completed) are always upcoming/overdue
    if (activity.activity_type === 'task') {
      const status = (activity.status || '').toLowerCase()
      return status !== 'completed' && status !== 'deferred'
    }
    // Future events are upcoming
    if (activity.activity_type === 'event' && activity.activity_date) {
      return !isBefore(toDisplayDate(activity.activity_date) ?? today, today)
    }
    return false
  }

  const isOverdue = (activity: Activity): boolean => {
    if (activity.activity_type === 'task' && activity.activity_date) {
      const status = (activity.status || '').toLowerCase()
      return status !== 'completed' && status !== 'deferred' && isPastDate(activity.activity_date)
    }
    return false
  }

  const upcomingActivities = activities.filter(isUpcomingOrOverdue).sort((a, b) => {
    // Overdue first, then by date ascending (nearest due first)
    const aOverdue = isOverdue(a) ? 0 : 1
    const bOverdue = isOverdue(b) ? 0 : 1
    if (aOverdue !== bOverdue) return aOverdue - bOverdue
    return (
      (toDisplayDate(a.activity_date)?.getTime() ?? 0) -
      (toDisplayDate(b.activity_date)?.getTime() ?? 0)
    )
  })

  const historyActivities = activities.filter((a) => !isUpcomingOrOverdue(a))

  // Group history by month
  const groupHistoryByMonth = (acts: Activity[]) => {
    const groups: { [key: string]: Activity[] } = {}
    acts.forEach((activity) => {
      const date = toDisplayDate(activity.activity_date) ?? new Date(0)
      const groupKey = format(date, 'MMMM yyyy')
      if (!groups[groupKey]) groups[groupKey] = []
      groups[groupKey]!.push(activity)
    })
    return groups
  }

  const historyGrouped = groupHistoryByMonth(historyActivities)
  const [upcomingExpanded, setUpcomingExpanded] = useState(true)

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-[#941052]" role="alert">
            <p>Error loading activities: {explainError(error, 'Unknown error')}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Stats and Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activity Timeline</CardTitle>
              {stats && (
                <p className="text-sm text-gray-600 mt-1">
                  {stats.total_activities} total activities
                  {stats.open_tasks > 0 && ` \u2022 ${stats.open_tasks} open tasks`}
                  {stats.upcoming_events > 0 && ` \u2022 ${stats.upcoming_events} upcoming events`}
                  {stats.calls > 0 && ` \u2022 ${stats.calls} calls`}
                </p>
              )}
            </div>

            {showFilters && (
              <div className="flex items-center gap-2">
                {/* Filter Menu */}
                <DropdownMenu open={showFilterMenu} onOpenChange={setShowFilterMenu}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" aria-label="Filter activities">
                      <Filter className="h-4 w-4 mr-2" aria-hidden="true" />
                      Filters
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-80 p-4"
                    aria-label="Activity filters"
                  >
                    <div className="space-y-4">
                      {/* Activity Type Filters */}
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Activity Types</Label>
                        <div className="space-y-2">
                          {(['task', 'event', 'email', 'call'] as ActivityType[]).map((type) => (
                            <div key={type} className="flex items-center space-x-2">
                              <Checkbox
                                id={`filter-${type}`}
                                checked={activityTypes.includes(type)}
                                onCheckedChange={() => handleTypeToggle(type)}
                                aria-label={`Show ${type}s`}
                              />
                              <Label
                                htmlFor={`filter-${type}`}
                                className="text-sm font-normal cursor-pointer capitalize"
                              >
                                {type}s ({stats?.[`${type}s` as keyof typeof stats] || 0})
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Date Range Filters */}
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Date Range</Label>
                        <div className="space-y-2">
                          <div>
                            <Label htmlFor="start-date" className="text-xs text-gray-600">
                              Start Date
                            </Label>
                            <DatePicker
                              id="start-date"
                              value={startDate}
                              onChange={(v) => {
                                setStartDate(v ?? '')
                                setPage(1)
                              }}
                              aria-label="Filter start date"
                            />
                          </div>
                          <div>
                            <Label htmlFor="end-date" className="text-xs text-gray-600">
                              End Date
                            </Label>
                            <DatePicker
                              id="end-date"
                              value={endDate}
                              onChange={(v) => {
                                setEndDate(v ?? '')
                                setPage(1)
                              }}
                              aria-label="Filter end date"
                            />
                          </div>
                          {(startDate || endDate) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleClearDateFilters}
                              className="w-full"
                            >
                              Clear Date Filters
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* TODO: Add Create New dropdown when modals are ready */}
                {/* <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" aria-label="Create new activity">
                      <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                      New
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem>Create Task</DropdownMenuItem>
                    <DropdownMenuItem>Create Event</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> */}
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden="true" />
          <span className="sr-only">Loading activities...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && activities.length === 0 && (
        <Card>
          <CardContent className="p-12">
            <div className="text-center text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" aria-hidden="true" />
              <p className="text-lg font-medium">No activities found</p>
              <p className="text-sm mt-1">
                {startDate || endDate
                  ? 'Try adjusting your date filters'
                  : 'Activities will appear here when created'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {!isLoading && activities.length > 0 && (
        <div className="space-y-6" role="feed" aria-label="Activity timeline">
          {/* Upcoming & Overdue Section */}
          {upcomingActivities.length > 0 && (
            <div>
              <button
                onClick={() => setUpcomingExpanded(!upcomingExpanded)}
                className="flex items-center gap-2 w-full text-left mb-3 px-1 focus:outline-none focus:ring-2 focus:ring-primary rounded"
                aria-expanded={upcomingExpanded}
                aria-controls="upcoming-overdue-section"
              >
                {upcomingExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                )}
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Upcoming & Overdue
                </h2>
                <Badge variant="outline" className="text-xs">
                  {upcomingActivities.length}
                </Badge>
              </button>
              {upcomingExpanded && (
                <div id="upcoming-overdue-section" className="space-y-2">
                  {upcomingActivities.map((activity) => (
                    <div key={activity.id} className="relative">
                      {isOverdue(activity) && (
                        <Badge
                          variant="destructive"
                          className="absolute -top-1 -right-1 z-10 text-[10px] px-1.5 py-0"
                        >
                          Overdue
                        </Badge>
                      )}
                      <ActivityTimelineItem
                        activity={activity}
                        onEditTask={handleEditTask}
                        onCompleteTask={setCompleting}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity History grouped by month */}
          {Object.entries(historyGrouped).map(([monthGroup, groupActivities]) => (
            <div key={monthGroup}>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 px-1">
                {monthGroup}
              </h2>
              <div className="space-y-2">
                {groupActivities.map((activity) => (
                  <ActivityTimelineItem
                    key={activity.id}
                    activity={activity}
                    onEditTask={handleEditTask}
                    onCompleteTask={setCompleting}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}{' '}
                activities
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Task Detail Modal */}
      <TaskDetailModal
        taskId={editingTaskId}
        open={taskModalOpen}
        onOpenChange={(open) => {
          setTaskModalOpen(open)
          if (!open) setEditingTaskId(null)
        }}
      />

      {/* Confirm completing a task from its status circle */}
      <AlertDialog
        open={!!completing}
        onOpenChange={(open) => {
          if (!open) setCompleting(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete this task?</AlertDialogTitle>
            <AlertDialogDescription>
              Mark &ldquo;{completing?.subject}&rdquo; as completed? You&rsquo;ll then be prompted
              to create a follow-up task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmComplete} disabled={updateTask.isPending}>
              Mark complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Follow-up task prompt (same as the Tasks list) */}
      {followUpTask && (
        <CreateTaskModal
          open={followUpModalOpen}
          onOpenChange={(open) => {
            setFollowUpModalOpen(open)
            if (!open) setFollowUpTask(null)
          }}
          defaultSubject={`Follow-up: ${followUpTask.subject}`}
          relatedToId={followUpTask.related_to?.what_id}
          relatedToType={
            followUpTask.related_to?.what_id?.startsWith('006')
              ? 'Opportunity'
              : followUpTask.related_to?.what_id?.startsWith('001')
                ? 'Account'
                : followUpTask.related_to?.what_id?.startsWith('500')
                  ? 'Ticket'
                  : undefined
          }
          relatedToName={followUpTask.related_to?.what_name}
          ownerId={followUpTask.owner_id}
          defaultContacts={
            followUpTask.related_to?.who_id && followUpTask.related_to?.who_name
              ? [
                  {
                    id: followUpTask.related_to.who_id,
                    name: followUpTask.related_to.who_name,
                    type: (followUpTask.related_to.who_id.startsWith('00Q')
                      ? 'Lead'
                      : 'Contact') as 'Contact' | 'Lead',
                  },
                ]
              : []
          }
        />
      )}
    </div>
  )
}
